import { useEffect, useEffectEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import dayjs from "dayjs";
import { useFlightSearchDataContext } from "@/app/lib/context/FlightSearchDataContext";
import { CabinClass, TripType } from "@/app/lib/types/enums";
import PageLoader from "@/app/ui/PageLoader";
import AnimatedLoader from "@/app/ui/AnimatedLoader";

const flightSearchSchema = z
  .object({
    departure: z.coerce
      .date()
      .refine((date) => !dayjs(date).isBefore(dayjs(), "day")),
    return: z.coerce.date(),
    originId: z.coerce.string(),
    originName: z.string(),
    originCountry: z.string(),
    destinationId: z.coerce.string(),
    destinationName: z.string(),
    destinationCountry: z.string(),
    adults: fallback(z.coerce.number(), 1).default(1),
    children: fallback(z.coerce.number(), 0).default(0),
    infants: fallback(z.coerce.number(), 0).default(0),
    tripType: fallback(z.nativeEnum(TripType), TripType.ONE_WAY).default(
      TripType.ONE_WAY,
    ),
    cabinClass: fallback(z.nativeEnum(CabinClass), CabinClass.ANY).default(
      CabinClass.ANY,
    ),
    utm_source: z.string().optional(),
    utm_campaign: z.string().optional(),
  })
  .refine(
    (data) => !dayjs(data.return).isBefore(dayjs(data.departure), "day"),
    {
      path: ["return"],
    },
  );

export const Route = createFileRoute("/_authenticated/flightsExternal")({
  component: RouteComponent,
  validateSearch: zodValidator(flightSearchSchema),
});

function RouteComponent() {
  const {
    departure,
    return: returnDate,
    originId,
    originName,
    originCountry,
    destinationId,
    destinationName,
    destinationCountry,
    adults,
    children,
    infants,
    tripType,
    cabinClass,
  } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { updateSearchData } = useFlightSearchDataContext();

  const setSearchData = useEffectEvent(() => {
    updateSearchData({
      from: {
        id: originId,
        name: originName,
        countryName: originCountry,
        type: "CITY",
      },
      to: {
        id: destinationId,
        name: destinationName,
        countryName: destinationCountry,
        type: "CITY",
      },
      dates: {
        departure,
        return: returnDate,
      },
      adult: adults,
      child: children,
      infantWithoutSeat: infants,
      tripType,
      cabinClass,
    });

    navigate({ to: "/flights/departure/list", replace: true });
  });

  useEffect(() => {
    setSearchData();
  }, []);

  return (
    <PageLoader>
      <AnimatedLoader icon="airplane" />
    </PageLoader>
  );
}
